import { useEffect, useMemo } from "react"; // Added useMemo if needed, useEffect for potential side-effects
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux"; // Added useDispatch if needed
import { Input, Row, Col, Spinner, Button } from "reactstrap"; // Added Spinner if any async ops are added
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
// Assuming App.css might be needed for consistent styling if not globally imported
// import "../../../App.css" // Or relevant path if this component is in a similar directory structure

// ReactQuill Modules and Formats (can be defined outside or inside the component)
const quillModules = {
    toolbar: [
        [{ 'font': [] }],
        [{ 'header': '1'}, { 'header': '2'}, { 'header': [3, 4, 5, 6, false] }],
        [{ 'size': ['small', false, 'large', 'huge'] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'script': 'sub'}, { 'script': 'super' }],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }, { 'indent': '-1'}, { 'indent': '+1' }],
        [{ 'align': [] }],
        ['blockquote', 'code-block'],
        ['link', 'image', 'video'],
        ['clean']
    ],
    clipboard: {
        matchVisual: false,
    }
};

const quillFormats = [
    'font', 'header', 'size',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background', 'script',
    'list', 'bullet', 'indent',
    'align', 'blockquote', 'code-block',
    'link', 'image', 'video'
];


const AddQuestionForm = ({
    labels,
    formikProps,
    isAddOrEdit, 
    // questionsScore,
    onTranslateClickHandler,
    translationLoading,      
    lastTranslatedField,     
}) => {
    const { t, i18n } = useTranslation();
    // const dispatch = useDispatch(); // Keep if needed for any async operations
    const isRTL = i18n.dir() === "rtl";

    const { values, errors, touched, handleChange, handleBlur, setFieldValue, setFieldTouched } = formikProps;

    const today = new Date().toISOString().split("T")[0];

    // Selectors for data needed by dropdowns
    const { topicsList, subTopics, QuestionTypes, DifficultyLevel } = useSelector((state) => ({
        topicsList: state.Topics.topicsList,
        subTopics: state.SubTopics.subTopics,
        QuestionTypes: state.Common.questionTypes,
        DifficultyLevel: state.Common.difficultyLevel
    }));

    // Filter subTopics based on selected TopicID from Formik values
    const filteredSubTopics = useMemo(() => {
        const topicId = values.TopicID; // Assuming 'TopicID' is the name of your topic dropdown field
        if (topicId && subTopics) {
            // Ensure TopicID is treated as a number for comparison if IDs are numbers
            return subTopics.filter(st => st.TopicID === Number(topicId));
        }
        return [];
    }, [values.TopicID, subTopics]);


    const renderField = (item) => {
        const fieldName = item.name; // 'name' from label config should be the formik field name
        const fieldValue = values[fieldName] ?? '';
        // Determine if field should be disabled
        // The 'disabled' property in the label config can be a boolean or a function
        const isDisabled = typeof item.disabled === 'function' ? item.disabled(isAddOrEdit, values) : !!item.disabled;

        switch (item.fieldName) {
            case 'text-box':
                return (
                    <Input
                        type="text"
                        id={fieldName}
                        name={fieldName}
                        placeholder={t(item.placeholder || item.labelName)}
                        value={fieldValue}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className="form-control"
                        invalid={touched[fieldName] && !!errors[fieldName]}
                        disabled={isDisabled}
                        required={item.required}
                    />
                );

            case 'text-box-translate':
            case 'textarea-translate':
                const InputComponent = item.fieldName === 'textarea-translate' || item.isTextArea ? 'textarea' : 'text';

                return (
                    <div className={`input-group ${isRTL ? 'rtl-input-group' : ''}`}>
                        <Input
                            type={InputComponent === 'textarea' ? 'textarea' : 'text'}
                            rows={item.fieldName === 'textarea-translate' || item.isTextArea ? (item.rows || "2") : undefined}
                            id={fieldName}
                            name={fieldName}
                            placeholder={t(item.placeholder || item.labelName)}
                            value={fieldValue}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className="form-control"
                            invalid={touched[fieldName] && !!errors[fieldName]}
                            disabled={isDisabled}
                            required={item.required}
                        />
                    {onTranslateClickHandler && item.translateTo && (
                        <Button
                            type="button"
                            onClick={() => onTranslateClickHandler(item.translateTo, fieldValue, setFieldValue, item.name)}
                            disabled={!fieldValue || !fieldValue.trim() || translationLoading}
                        >
                            {translationLoading && lastTranslatedField === item.name ? (
                            <Spinner size="sm" />
                            ) : (
                            <i className="ri-translate"></i>
                            )}
                        </Button>
                    )}
                    </div>
                );

            case 'textarea-box':
                return (
                    <Input
                        type="textarea"
                        rows={item.rows || "3"} // Default to 3 rows if not specified
                        id={fieldName}
                        name={fieldName}
                        placeholder={t(item.placeholder || item.labelName)}
                        value={fieldValue}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className="form-control"
                        invalid={touched[fieldName] && !!errors[fieldName]}
                        disabled={isDisabled}
                        required={item.required}
                    />
                );

            case 'number-box': // For scores or other numeric inputs
                return (
                    <Input
                        type="number" // Use number type for better UX on mobile
                        id={fieldName}
                        name={fieldName}
                        placeholder={t(item.placeholder || item.labelName)}
                        value={fieldValue}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className="form-control"
                        invalid={touched[fieldName] && !!errors[fieldName]}
                        disabled={isDisabled || item.isScoreField} // Example: if item specifies it's a score field and should be disabled
                        required={item.required}
                        style={{ textAlign: isRTL ? 'right' : 'left' }}
                    />
                );

            case 'date':
                return (
                    <Input
                        type="date"
                        id={fieldName}
                        name={fieldName}
                        min={item.minToday ? today : item.min}
                        max={item.max}
                        value={(fieldValue || '').split('T')[0]} // Ensure yyyy-mm-dd format
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className="form-control"
                        invalid={touched[fieldName] && !!errors[fieldName]}
                        disabled={isDisabled}
                        required={item.required}
                    />
                );

            case 'topic-dropdown':
                return (
                    <Input
                        type="select"
                        id={fieldName}
                        name={fieldName}
                        value={fieldValue}
                        onChange={(e) => {
                            handleChange(e);
                            // Optionally, clear subtopic when topic changes
                            if (item.dependentClearField) { // e.g., dependentClearField: "SubTopicID" in label config
                                setFieldValue(item.dependentClearField, "");
                            }
                        }}
                        onBlur={handleBlur}
                        className="form-control"
                        invalid={touched[fieldName] && !!errors[fieldName]}
                        disabled={isDisabled}
                        required={item.required}
                    >
                        <option value="">{t(item.defaultOptionLabel || "Choose")}</option>
                        {(topicsList || []).map((option) => (
                            <option
                                key={option.ID}
                                value={option.ID}
                            >
                                {i18n.language === 'en' ? option.TopicNameEn : option.TopicNameAr}
                            </option>
                        ))}
                    </Input>
                );

            case 'sub-topic-dropdown':
                return (
                    <Input
                        type="select"
                        id={fieldName}
                        name={fieldName}
                        value={fieldValue}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className="form-control"
                        invalid={touched[fieldName] && !!errors[fieldName]}
                        disabled={isDisabled || !values.TopicID} // Disable if no topic is selected
                        required={item.required}
                    >
                        <option value="">{t(item.defaultOptionLabel || "Choose")}</option>
                        {/* Uses pre-filtered subTopics based on values.TopicID */}
                        {(filteredSubTopics || []).map((option) => (
                            <option
                                key={option.ID} // Assuming SubTopic items also have an 'ID'
                                value={option.ID}
                            >
                                {i18n.language === 'en' ? option.SubTopicNameEn : option.SubTopicNameAr}
                            </option>
                        ))}
                    </Input>
                );

            case 'type-dropdown': // Question Type Dropdown
                return (
                    <Input
                        type="select"
                        id={fieldName}
                        name={fieldName}
                        value={fieldValue}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className="form-control"
                        invalid={touched[fieldName] && !!errors[fieldName]}
                        disabled={isDisabled}
                        required={item.required}
                    >
                        <option value="">{t(item.defaultOptionLabel || "Choose")}</option>
                        {(QuestionTypes || []).map((option) => (
                            <option
                                key={option.ID}
                                value={option.ID}
                            >
                                {i18n.language === 'en' ? option.NameEn : option.NameAr}
                            </option>
                        ))}
                    </Input>
                );

            case 'level-dropdown': // Difficulty Level Dropdown
                return (
                    <Input
                        type="select"
                        id={fieldName}
                        name={fieldName}
                        value={fieldValue}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className="form-control"
                        invalid={touched[fieldName] && !!errors[fieldName]}
                        disabled={isDisabled}
                        required={item.required}
                    >
                        <option value="">{t(item.defaultOptionLabel || "Choose")}</option>
                        {(DifficultyLevel || []).map((option) => (
                            <option
                                key={option.ID}
                                value={option.ID}
                            >
                                {i18n.language === 'en' ? option.NameEn : option.NameAr}
                            </option>
                        ))}
                    </Input>
                );
            
            case 'editor': 
                 // Add a specific class for Quill if borders/errors need custom styling
                const quillClassName = `quill-editor-container ${touched[fieldName] && !!errors[fieldName] ? 'is-invalid' : ''}`;
                return (
                    <div className={quillClassName}>
                        <ReactQuill
                            theme="snow"
                            id={fieldName} // Not directly used by Quill but good for association
                            value={fieldValue}
                            onChange={(content, delta, source, editor) => {
                                setFieldValue(fieldName, content);
                            }}
                            onBlur={(previousRange, source, editor) => {
                                // Set field as touched. Formik will then show validation errors if any.
                                // Pass the current value to ensure validation runs with the latest content.
                                setFieldTouched(fieldName, true);
                                // Optionally, if you want to validate on blur explicitly with the editor's text:
                                // setFieldValue(fieldName, editor.getHTML(), true); // true for validate
                            }}
                            modules={quillModules}
                            formats={quillFormats}
                            placeholder={t(item.placeholder || item.labelName)}
                            readOnly={isDisabled} // Use readOnly prop for Quill
                        />
                         {/* 
                           Error message will be displayed below by the common error display logic.
                           The .is-invalid class on the container can be used to style the border.
                           You might need CSS like:
                           .quill-editor-container.is-invalid .ql-toolbar { border-color: #dc3545 !important; }
                           .quill-editor-container.is-invalid .ql-container { border-color: #dc3545 !important; }
                        */}
                    </div>
                );

            default:
                return <p>Unsupported field type: {item.fieldName}</p>;
        }
    };

    return (
        <Row>
            {labels.map((item) => {
                const fieldName = item.name;
                // By default, show label. Can be configured via item.showLabel = false in label config.
                const showLabel = item.showLabel !== false; 

                return (
                    <Col sm={item.colSize || 6} key={fieldName} className={item.className}>
                        <div className="mb-3">
                            {showLabel && (
                                <label htmlFor={fieldName} className="col-form-label">
                                    {t(item.labelName)}
                                    {item.required && <span className="text-danger"> *</span>}
                                </label>
                            )}
                            {renderField(item)}
                            {touched[fieldName] && errors[fieldName] && (
                                <div className="text-danger mt-1" style={{ fontSize: "0.875rem" }}>
                                    {/* Ensure errors are translated if they are keys */}
                                    {typeof errors[fieldName] === 'string' ? t(errors[fieldName]) : errors[fieldName]}
                                </div>
                            )}
                        </div>
                    </Col>
                );
            })}
        </Row>
    );
};

export default AddQuestionForm;